// src/components/Header.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("interior");

  const dropdownRef = useRef(null);

  const productData = {
    interior: {
      name: "Interior Paints",
      items: [
        { name: "Proflex", link: "/product/proflex-interior" },
        { name: "Neotherm AC", link: "/product/neotherm-ac" }
      ]
    },
    exterior: {
      name: "Exterior Paints",
      items: [
        { name: "Proflex", link: "/product/proflex-exterior" },
        { name: "Silatex Reflect", link: "/product/silatex-reflect" },
        { name: "Revinex Elastic", link: "/product/revinex-elastic" }
      ]
    },
    primers: {
      name: "Primers & Base Layers",
      items: [
        { name: "Acqua Primer", link: "/product/acqua-primer" },
        { name: "Revinex", link: "/product/revinex-primer" },
        { name: "Neotex Inox Primer", link: "/product/neotex-inox-primer" }
      ]
    },
    decorative: {
      name: "Specialty Decorative Finishes",
      items: [
        { name: "Neodur Varnish W Mat", link: "/product/neodur-varnish-w-mat" },
        { name: "Neodur Varnish W Satin", link: "/product/neodur-varnish-w-satin" },
        { name: "Neodur Varnish W Gloss", link: "/product/neodur-varnish-w-gloss" },
        { name: "Neocryl Sport Flex", link: "/product/neocryl-sport-flex" },
        { name: "Neopox W Plus", link: "/product/neopox-w-plus" }
      ]
    }
  };

  // Close dropdown outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50">

      {/* 🔥 TOP BAR */}
      <div className="bg-black text-white text-sm py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-end">
          info@everkast.co.nz
        </div>
      </div>

      {/* 🔥 MAIN HEADER */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-28 md:h-32">

  {/* Logo */}
  <Link to="/" className="flex items-center">
    <img 
      src="/images/logo.png" 
      alt="Everkast NZ" 
      className="h-20 md:h-24 lg:h-28 w-auto object-contain"
    />
  </Link>


            {/* DESKTOP NAV */}
            <nav className="hidden md:flex items-center space-x-10 font-medium">

              <Link className="hover:text-blue-600 transition" to="/">Home</Link>

              {/* 🔥 CLICK DROPDOWN */}
              <div ref={dropdownRef} className="relative">

                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1 hover:text-blue-600 transition"
                >
                  Products
                  <ChevronDown
                    size={16}
                    className={`transition ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* MAIN DROPDOWN */}
                {isDropdownOpen && (
                  <div className="absolute left-0 top-full mt-5 w-72 bg-white rounded-xl shadow-2xl border z-50">

                    {Object.entries(productData).map(([key, category]) => (
                      <div key={key} className="relative">

                        {/* CATEGORY */}
                        <div
                          onClick={() => setActiveCategory(key)}
                          className={`px-5 py-3 flex justify-between cursor-pointer transition ${
                            activeCategory === key
                              ? "bg-gray-100 text-blue-600 font-semibold"
                              : "hover:bg-gray-100"
                          }`}
                        >
                          {category.name}
                          <span>›</span>
                        </div>

                        {/* FLOATING SUBMENU */}
                        {activeCategory === key && (
                          <div className="absolute left-[100%] top-0 w-60 bg-white rounded-xl shadow-2xl border p-3 animate-fadeIn">

                            <div className="font-semibold mb-3 border-b pb-2">
                              {category.name}
                            </div>

                            {category.items.map((item, i) => (
                              <Link
                                key={i}
                                to={item.link}
                                className="block px-2 py-1 rounded-md hover:bg-gray-100 hover:text-blue-600 transition"
                              >
                                {item.name}
                              </Link>
                            ))}

                          </div>
                        )}

                      </div>
                    ))}

                  </div>
                )}

              </div>

              <Link className="hover:text-blue-600 transition" to="/about">About</Link>
              <Link className="hover:text-blue-600 transition" to="/contact">Contact</Link>

              {/* 🔥 DATASHEET BUTTON */}
              <Link
                to="/datasheets"
                className="px-5 py-2 rounded-lg text-white bg-black shadow-md hover:shadow-xl hover:scale-105 transition"
              >
                Technical Datasheets
              </Link>

              {/* 🔥 QUOTE BUTTON */}
              <Link
                to="/request-quote"
                className="px-5 py-2 rounded-lg text-white bg-blue-600 shadow-lg hover:shadow-2xl hover:scale-105 transition"
              >
                Request Quote
              </Link>

            </nav>

            {/* MOBILE BUTTON */}
            <button
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>

          </div>
        </div>
      </div>

      {/* 🔥 PURPLE DIVIDER */}
      <div className="h-2 bg-gradient-to-r from-indigo-500 via-fuchsia-400 to-orange-400"></div>

      {/* 🔥 MOBILE MENU */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg px-4 py-4 space-y-3">

          <Link to="/">Home</Link>

          {/* MOBILE PRODUCTS */}
          <div>
            <div className="font-semibold mb-2">Products</div>
            {Object.entries(productData).map(([key, category]) => (
              <div key={key}>
                <div
                  onClick={() => setActiveCategory(key)}
                  className="py-2 font-medium"
                >
                  {category.name}
                </div>

                {activeCategory === key && (
                  <div className="pl-4 text-sm space-y-1">
                    {category.items.map((item, i) => (
                      <Link key={i} to={item.link} className="block">
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Link to="/about">About</Link>
          <Link to="/contact">Contact Us</Link>
          <Link to="/datasheets">Technical Datasheets</Link>
          <Link to="/request-quote">Request Quote</Link>

        </div>
      )}

    </header>
  );
};

export default Header;